import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { makeFetchRequest, makePutRequest } from "../utilities/common";
import {
  uploadAccept,
  uploadImageToCloudinary,
  validateUploadFile,
} from "../utilities/importantFns";
import { defaultUrl } from "../features/actions";
import { Building2, Phone, MapPin, UploadCloud, Save, X } from "lucide-react";
import toast from "react-hot-toast";

const SchProfileSetting = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [imageUploaded, setImageUploaded] = useState<any>({});
  const [preview, setPreview] = useState<any>({});

  useEffect(() => {
    makeFetchRequest(`${defaultUrl}/school`).then(setForm);
  }, []);

  const handleImageUpload = (e: any) => {
    const file = e.target.files[0];
    const name = e.target.name;
    if (file) {
      const validation = validateUploadFile(file, "image");
      if (!validation.valid) {
        toast.error(validation.message);
        e.target.value = "";
        return;
      }
      setImageUploaded((prev: any) => ({ ...prev, [name]: file }));
      setPreview((prev: any) => ({
        ...prev,
        [name]: URL.createObjectURL(file),
      }));
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    const toastId = toast.loading("Updating profile...");

    try {
      let logoUrl = form.school_logo;
      let signatureUrl = form.school_head_signature;

      if (imageUploaded.school_logo)
        logoUrl = await uploadImageToCloudinary(imageUploaded.school_logo);
      if (imageUploaded.school_head_signature)
        signatureUrl = await uploadImageToCloudinary(
          imageUploaded.school_head_signature,
        );

      const body = {
        ...form,
        school_logo: logoUrl,
        school_head_signature: signatureUrl,
      };

      await makePutRequest(`${defaultUrl}/school`, body);
      toast.success("Profile updated successfully", { id: toastId });
    } catch (err) {
      toast.error("Update failed", { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  // UI Styles based on your image
  const labelStyle =
    "text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block ml-1";
  const inputContainer =
    "relative flex items-center bg-[#F8FAFC] rounded-[1.5rem] border border-transparent focus-within:border-blue-100 transition-all px-6 py-4";
  const inputStyle =
    "w-full bg-transparent outline-none text-slate-700 font-bold text-sm ml-4 placeholder:text-slate-300";

  return (
    <form onSubmit={handleSubmit} className="animate-in fade-in duration-500">
      {/* Header Info */}
      <div className="flex flex-col md:flex-row justify-between items-start mb-12">
        <div className="max-w-md">
          <h2 className="text-[42px] font-black text-[#1E293B] leading-tight mb-2">
            School <br /> Identity
          </h2>
          <p className="text-slate-400 font-bold text-sm leading-relaxed">
            Manage your school's public profile and branding.
          </p>
        </div>

        <div className="flex items-center gap-8 mt-6 md:mt-0">
          <button
            type="button"
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2 font-black text-slate-400 hover:text-slate-600 transition-colors uppercase text-xs tracking-widest"
          >
            <X size={18} /> Cancel
          </button>

          <button
            type="submit"
            disabled={loading}
            className="bg-[#0F172A] text-white px-10 py-5 rounded-[1.5rem] font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-2xl flex items-center gap-3"
          >
            <Save size={16} />
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left Column: Form Fields */}
        <div className="lg:col-span-7 space-y-10">
          {/* Institution Name */}
          <div className="space-y-3">
            <label className={labelStyle}>Full Name of Institution</label>
            <div className={inputContainer}>
              <Building2 className="text-slate-300" size={20} />
              <input
                type="text"
                name="school_name"
                value={form.school_name || ""}
                onChange={(e) =>
                  setForm({ ...form, school_name: e.target.value })
                }
                placeholder="Enter school name"
                className={inputStyle}
              />
            </div>
          </div>

          {/* Phone Number */}
          <div className="space-y-3">
            <label className={labelStyle}>Primary Phone Number</label>
            <div className={`${inputContainer} max-w-xs`}>
              <Phone className="text-slate-300" size={18} />
              <input
                type="tel"
                name="phone_number"
                value={form.phone_number || ""}
                onChange={(e) =>
                  setForm({ ...form, phone_number: e.target.value })
                }
                placeholder="0706779..."
                className={inputStyle}
              />
            </div>
          </div>

          {/* Physical Address */}
          <div className="space-y-3">
            <label className={labelStyle}>Physical Address</label>
            <div className={inputContainer}>
              <MapPin className="text-slate-300" size={18} />
              <input
                type="text"
                name="address"
                value={form.address || ""}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Enter address"
                className={inputStyle}
              />
            </div>
          </div>

          {/* Signature */}
          <div className="space-y-3">
            <label className={labelStyle}>
              Authorized Signature (Head Teacher)
            </label>
            <label className="group relative block w-full max-w-sm h-32 bg-[#F8FAFC] rounded-[2rem] cursor-pointer border-2 border-dashed border-slate-100 hover:border-blue-200 transition-all overflow-hidden flex items-center justify-center">
              {preview.school_head_signature || form.school_head_signature ? (
                <img
                  src={
                    preview.school_head_signature || form.school_head_signature
                  }
                  className="h-full w-auto object-contain p-4"
                  alt="signature"
                />
              ) : (
                <div className="text-slate-300 font-bold text-[10px] uppercase tracking-widest flex items-center gap-2">
                  <UploadCloud size={18} /> Upload Signature
                </div>
              )}
              <input
	                type="file"
	                name="school_head_signature"
	                accept={uploadAccept.image}
	                onChange={handleImageUpload}
	                className="hidden"
              />
            </label>
          </div>
        </div>

        {/* Right Column: Logo Upload */}
        <div className="lg:col-span-5 flex flex-col items-center lg:items-end">
          <div className="w-full max-w-[280px]">
            <label className={`${labelStyle} text-center mb-6`}>
              Official School Logo
            </label>

            <label className="group relative block aspect-square bg-white rounded-[3.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.08)] border-8 border-white overflow-hidden cursor-pointer active:scale-95 transition-all">
              {preview.school_logo || form.school_logo ? (
                <img
                  src={preview.school_logo || form.school_logo}
                  className="w-full h-full object-contain p-6"
                  alt="logo"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-200">
                  <UploadCloud size={40} />
                </div>
              )}

              <div className="absolute inset-0 bg-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity" />
              <input
	                type="file"
	                name="school_logo"
	                accept={uploadAccept.image}
	                onChange={handleImageUpload}
	                className="hidden"
              />
            </label>

            <p className="mt-8 text-center text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] leading-relaxed opacity-70">
              Recommended size: <br />
              512x512px (PNG or JPG)
            </p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default SchProfileSetting;
